import Big from 'big.js';
import {merge, combineLatest} from 'rxjs';
import {scan, map, filter} from 'rxjs/operators';
import {gt, gte} from '@/helper/num';
import {contractUtil} from '@/helper/contract';
import {contractDefaultTakerFeeRateEr$, riskLimitCache$} from '@/config';
import {contract$} from '@/state/contract';
import {leverageMarginsCache$} from '@/config';
import {wsLogout$} from '@/user/auth-ws';
import {aopMsg$, aopMsgV2$} from '@/raw/aop';
import type {ws, ContractPair, ReduceAction, ContractRiskLimit, LeverageMargin} from '@/types';

type ContractPositionRaw = ws.ContractPositionRaw;
type Cache = Map<string, ContractPositionRaw>;
type Action = ReduceAction<Cache>;

interface LeverageDataProps {
  leverageEr: number;
  isIsolated: boolean;
  isCross: boolean;
}

export const positions$ = aopMsg$.pipe(
  filter((o) => !!o.positions),
  map(({positions}) => positions.map((o) => ({...completionPosition(o), ...o})))
);

export const positionsV2$ = aopMsgV2$.pipe(
  filter((o) => !!o.positions),
  map(({positions}) => convertPositionV2(positions))
);

// 只初始化当前交易对的合约信息
const initAction$ = combineLatest([
  contract$,
  riskLimitCache$,
  leverageMarginsCache$,
  contractDefaultTakerFeeRateEr$,
]).pipe(
  map(([contract, riskLimits, leverageMargins, defaultTakerFeeRateEr]) =>
    convertPosition(contract, riskLimits, leverageMargins, defaultTakerFeeRateEr)
  )
);

const resetAction$ = wsLogout$.pipe<Action>(
  map(
    () =>
      function (cache: Cache) {
        cache.clear();
        return cache;
      }
  )
);

const updateAction$ = positions$.pipe(
  map((positions: ContractPositionRaw[]): Action => {
    return function (cache: Cache) {
      positions.forEach((position: ContractPositionRaw) => {
        cache.set(position.symbol, {
          type: 'Perpetual',
          ...cache.get(position.symbol),
          ...position,
          ...convertLeverageData(position, cache.get(position.symbol)),
        });
      });
      return cache;
    };
  })
);

const updateAction2$ = positionsV2$.pipe(
  map((positions: ContractPositionRaw[]): Action => {
    return function (cache: Cache) {
      positions.forEach((position: ContractPositionRaw) => {
        const {symbol, posSide, posMode} = position;
        const key = symbol + posSide;
        ['Merged', 'Long', 'Short'].forEach((side) => {
          const key2 = symbol + side;
          cache.has(key2) && cache.set(key2, {...cache.get(key2), posMode});
        });
        cache.set(key, {
          type: 'PerpetualV2',
          ...cache.get(key),
          ...position,
          ...convertLeverageData(position, cache.get(key)),
        });
      });
      return cache;
    };
  })
);

export const positionsCache$ = merge<Action[]>(initAction$, resetAction$, updateAction$, updateAction2$).pipe(
  scan((p, action: Action) => action(p), new Map<string, ContractPositionRaw>())
);

export function convertPosition(
  contract: ContractPair,
  riskLimitCache: Map<string, ContractRiskLimit>,
  leverageMarginCache: Map<string, LeverageMargin>,
  defaultTakerFeeRateEr: number
) {
  return function (cache: Cache) {
    const {type, symbol, settleCurrency, ratioFactor, defaultLeverage = '-10', leverageMargin} = contract;
    const {riskLimitEv, initialMarginEr} = genRisk(type, symbol, leverageMargin, riskLimitCache, leverageMarginCache);
    const posSides = type === 'Perpetual' ? ['Merged'] : ['Merged', 'Long', 'Short'];
    const defaultTakerFeeRateEr2 = contractUtil.getDefaultTakerFeeRate(contract, defaultTakerFeeRateEr);
    const positions = posSides.map((posSide) =>
      initPosition(
        type,
        posSide,
        symbol,
        riskLimitEv,
        settleCurrency,
        defaultLeverage,
        initialMarginEr,
        ratioFactor,
        defaultTakerFeeRateEr2
      )
    );
    return positions.reduce((p, position) => {
      const {type, symbol, posSide} = position;
      const key = type === 'Perpetual' ? symbol : symbol + posSide;
      return p.has(key) ? p : p.set(key, position);
    }, cache);
  };
}

function genRisk(type, symbol, leverageMargin, riskLimitCache, leverageMarginCache) {
  if (type === 'Perpetual') {
    const item = riskLimitCache.get(symbol);
    const data = item.riskLimits;
    if (data.length > 0) {
      const {riskLimitEv, initialMarginEr} = data[0];
      return {riskLimitEv, initialMarginEr};
    }
  } else {
    const leverageItem = leverageMarginCache.get('id_' + leverageMargin);
    const data = leverageItem.items;
    if (data.length > 0) {
      const {notionalValueRv, maxLeverage} = data[0];
      return {
        riskLimitEv: notionalValueRv || 0,
        initialMarginEr: Big(1).div(maxLeverage).round(2, 0).toNumber(),
      };
    }
  }
  return {riskLimitEv: 0, initialMarginEr: 0};
}

function initPosition(
  type: string,
  posSide: string,
  symbol: string,
  riskLimitEv: number,
  settleCurrency: string,
  leverage: string,
  initMarginReqEr: number,
  ratioFactor: number,
  takerFeeRate: number
): ContractPositionRaw {
  // (1 / leverage + takerFeeRate * (2 - 1 / leverage)) * 1e8;
  // (1 / leverage + takerFeeRate * (2 + 1 / leverage)) * 1e8;
  const realLeverage = Math.abs(Number(leverage));
  const leverageBig = Big(1).div(realLeverage);
  const buyValueToCostEr = Number(
    leverageBig.plus(Big(takerFeeRate).times(Big(2).minus(leverageBig))).times(ratioFactor)
  );
  const sellValueToCostEr = Number(
    leverageBig.plus(Big(takerFeeRate).times(Big(2).plus(leverageBig))).times(ratioFactor)
  );
  const leverageEr = Number(Big(realLeverage).times(ratioFactor));
  return {
    accountID: 0,
    assignedPosBalanceEv: 0,
    avgEntryPriceEp: 0,
    bankruptCommEv: 0,
    bankruptPriceEp: 0,
    buyLeavesQty: 0,
    buyLeavesValueEv: 0,
    buyValueToCostEr,
    createdAtNs: 0,
    crossSharedBalanceEv: 0,
    cumClosedPnlEv: 0,
    cumFundingFeeEv: 0,
    cumTransactFeeEv: 0,
    curTermRealisedPnlEv: 0,
    curTermDiscountFeeEv: 0,
    currency: settleCurrency,
    dataVer: 3,
    deleveragePercentileEr: 0,
    displayLeverageEr: leverageEr,
    estimatedOrdLossEv: 0,
    execSeq: 0,
    freeCostEv: 0,
    freeQty: 0,
    initMarginReqEr,
    lastFundingTime: 0,
    lastTermEndTime: 0,
    leverageEr,
    isCross: gte(0, leverage),
    isIsolated: gt(leverage, 0),
    liquidationPriceEp: 0,
    maintMarginReqEr: 0,
    makerFeeRateEr: 0,
    markPriceEp: 0,
    orderCostEv: 0,
    posCostEv: 0,
    positionMarginEv: 0,
    positionStatus: 'Normal',
    riskLimitEv,
    sellLeavesQty: 0,
    sellLeavesValueEv: 0,
    sellValueToCostEr,
    // @ts-ignore
    side: 'None',
    size: 0,
    symbol,
    takerFeeRateEr: 0,
    term: 1,
    transactTimeNs: 0,
    unrealisedPnlEv: 0,
    updatedAtNs: 0,
    usedBalanceEv: 0,
    userID: 0,
    valueEv: 0,
    posMode: type === 'PerpetualV2' ? 'Hedged' : 'OneWay',
    // @ts-ignore
    posSide,
    // @ts-ignore
    type,
  };
}

export function convertLeverageData(
  position: ContractPositionRaw,
  cachePosition: ContractPositionRaw
): LeverageDataProps {
  const leverageEr = position.leverageEr === undefined ? cachePosition?.leverageEr : position.leverageEr;
  return {
    leverageEr: Math.abs(leverageEr),
    isIsolated: leverageEr > 0,
    isCross: leverageEr <= 0,
  };
}

export function convertPositionV2(positions) {
  return positions.map((o) => {
    return {
      ...o,
      accountID: o.accountID,
      assignedPosBalanceEv: Number(o.assignedPosBalanceRv) || 0,
      avgEntryPriceEp: Number(o.avgEntryPriceRp) || 0,
      bankruptCommEv: Number(o.bankruptCommRv) || 0,
      bankruptPriceEp: Number(o.bankruptPriceRp) || 0,
      buyLeavesQty: Number(o.buyLeavesQty) || 0,
      buyLeavesValueEv: Number(o.buyLeavesValueRv) || 0,
      buyValueToCostEr: Number(o.buyValueToCostRr) || 0,
      createdAtNs: Number(o.createdAtNs) || 0,
      crossSharedBalanceEv: Number(o.crossSharedBalanceRv) || 0,
      cumClosedPnlEv: Number(o.cumClosedPnlRv) || 0,
      cumFundingFeeEv: Number(o.cumFundingFeeRv) || 0,
      cumTransactFeeEv: Number(o.cumTransactFeeRv) || 0,
      curTermRealisedPnlEv: Number(o.curTermRealisedPnlRv) || 0,
      cumPtFeeEv: Number(o.cumPtFeeRv) || 0,
      curTermDiscountFeeEv: Number(o.curTermDiscountFeeRv) || 0,
      curTermPtFeeEv: Number(o.curTermPtFeeRv) || 0,
      currency: o.currency,
      dataVer: o.dataVer || 0,
      deleveragePercentileEr: Number(o.deleveragePercentileRr) || 0,
      displayLeverageEr: Number(o.displayLeverageRr) || 0,
      estimatedOrdLossEv: Number(o.estimatedOrdLossRv) || 0,
      execSeq: Number(o.execSeq) || 0,
      freeCostEv: Number(o.freeCostRv) || 0,
      freeQty: Number(o.freeQty) || 0,
      initMarginReqEr: Number(o.initMarginReqRr) || 0,
      lastFundingTime: Number(o.lastFundingTime) || 0,
      lastTermEndTime: Number(o.lastTermEndTime) || 0,
      leverageEr: Number(o.leverageRr) || 0,
      liquidationPriceEp: Number(o.liquidationPriceRp) || 0,
      maintMarginReqEr: Number(o.maintMarginReqRr) || 0,
      makerFeeRateEr: Number(o.makerFeeRateRr) || 0,
      markPriceEp: Number(o.markPriceRp) || 0,
      orderCostEv: Number(o.orderCostRv) || 0,
      posCostEv: Number(o.posCostRv) || 0,
      positionMarginEv: Number(o.positionMarginRv) || 0,
      positionStatus: o.positionStatus,
      riskLimitEv: Number(o.riskLimitRv) || 0,
      sellLeavesQty: Number(o.sellLeavesQty) || 0,
      sellLeavesValueEv: Number(o.sellLeavesValueRv) || 0,
      sellValueToCostEr: Number(o.sellValueToCostRr) || 0,
      side: o.side,
      size: Number(o.size),
      symbol: o.symbol,
      takerFeeRateEr: Number(o.takerFeeRateRr) || 0,
      term: Number(o.term) || 0,
      transactTimeNs: Number(o.transactTimeNs) || 0,
      unrealisedPnlEv: Number(o.unrealisedPnlRv) || 0,
      updatedAtNs: Number(o.updatedAtNs) || 0,
      usedBalanceEv: Number(o.usedBalanceRv) || 0,
      userID: Number(o.userID) || 0,
      valueEv: Number(o.valueRv) || 0,
      minPosCostRv: Number(o.minPosCostRv) || 0,
      posMode: o.posMode,
      posSide: o.posSide,
    };
  });
}

function completionPosition({accountID, currency, symbol, side}) {
  return {
    side,
    symbol,
    currency,
    accountID,
    assignedPosBalanceEv: 0,
    avgEntryPriceEp: 0,
    bankruptCommEv: 0,
    bankruptPriceEp: 0,
    buyLeavesQty: 0,
    buyLeavesValueEv: 0,
    buyValueToCostEr: 0,
    createdAtNs: 0,
    crossSharedBalanceEv: 0,
    cumClosedPnlEv: 0,
    cumFundingFeeEv: 0,
    cumTransactFeeEv: 1,
    curTermRealisedPnlEv: -1,
    dataVer: 0,
    deleveragePercentileEr: 0,
    displayLeverageEr: 0,
    estimatedOrdLossEv: 0,
    execSeq: 0,
    freeCostEv: 0,
    freeQty: 0,
    initMarginReqEr: 0,
    lastFundingTime: 0,
    lastTermEndTime: 0,
    leverageEr: 0,
    liquidationPriceEp: 0,
    maintMarginReqEr: 0,
    makerFeeRateEr: 0,
    markPriceEp: 0,
    orderCostEv: 0,
    posCostEv: 0,
    positionMarginEv: 0,
    positionStatus: 'Normal',
    riskLimitEv: 0,
    sellLeavesQty: 0,
    sellLeavesValueEv: 0,
    sellValueToCostEr: 0,
    size: 0,
    takerFeeRateEr: 0,
    term: 0,
    transactTimeNs: 0,
    unrealisedPnlEv: 0,
    updatedAtNs: 0,
    usedBalanceEv: 0,
    userID: 0,
    valueEv: 0,
    marginRatioRr: '0',
    marginRatio: '--',
  };
}
